





























import {
  CourseDetails,
  UpsertCoursePackageApiDTO
} from '@/jbi-shared/dto/jtms.dtos';
import { CoursePackageStatus } from '@/jbi-shared/types/jtms.types';
import { Debounce } from '@/jbi-shared/util/debounce.vue-decorator';
import {
  CourseModuleComponentItem,
  IndexBooleanPayload
} from '@/store/modules/jtms/course-module/course-module.types';
import {
  CoursePackageComponent,
  defaultCoursePackage,
  UpdateCoursePackageStatePayload,
  UpsertCoursePackageResponse
} from '@/store/modules/jtms/course-package/course-package.types';
import { RootState } from '@/store/store';
import { ApiState } from '@/store/types/general.types';
import FieldWrapper from '@/views/AdminGroupSettings/components/GroupLevelAttributes/FieldWrapper.vue';
import { ToastProgrammatic as Toast } from 'buefy';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Action, State } from 'vuex-class';
import CoursePackageComponentItem from './components/CoursePackageComponentItem.vue';

@Component({
  components: {
    FieldWrapper,
    CoursePackageComponentItem
  }
})
export default class CoursePackagesTab extends Vue {
  @Prop(Boolean) isPackageTabOpened!: boolean;
  @Prop({ default: false }) isSettings!: boolean;

  @Action('coursePackage/upsertCoursePackages')
  upsertCoursePackages!: (payload: UpsertCoursePackageApiDTO) => void;

  @Action('coursePackage/deleteCoursePackages')
  deleteCoursePackages!: (payload: number[]) => void;

  @Action('coursePackage/addActiveCoursePackage')
  addActiveCoursePackage!: (payload: CoursePackageComponent) => void;

  @Action('coursePackage/removeActiveCoursePackage')
  removeActiveCoursePackage!: (index: number) => void;

  @Action('courseModule/addDeletedCourseModule')
  addDeletedCourseModule!: (payload: CourseModuleComponentItem) => void;

  @Action('courseModule/updateCourseModuleFormError')
  updateCourseModuleFormError!: (payload: boolean) => void;

  @Action('courseModule/updateCourseModulePrerequisiteDropdownState')
  updateCourseModulePrerequisiteDropdownState!: (
    payload: IndexBooleanPayload
  ) => void;

  @Action('coursePackage/updateCoursePackage')
  updateCoursePackage!: (payload: UpdateCoursePackageStatePayload) => void;

  @State((state: RootState) => state.coursePackage.upsertedCoursePackage)
  upsertedCoursePackage!: UpsertCoursePackageResponse;

  @State(
    (state: RootState) => state.coursePackage.apiState.upsertCoursePackageApi
  )
  upsertCoursePackageApiState!: ApiState;

  @State((state: RootState) => state.course.courseDetails)
  courseDetails!: CourseDetails;

  @State(
    (state: RootState) =>
      state.coursePackage.coursePackageTabState.activeCoursePackages
  )
  activeCoursePackages!: CoursePackageComponent[];

  @State((state: RootState) => state.course.courseCreationState.hasModules)
  hasModules!: boolean;

  @State(
    (state: RootState) => state.coursePackage.apiState.deleteCoursePackageApi
  )
  deleteCoursePackageApiState!: ApiState;

  get hasDefaultCoursePackage() {
    if (!this.activeCoursePackages) {
      return false;
    }

    return this.activeCoursePackages.some(
      (pkg) => pkg.status === CoursePackageStatus.DEFAULT
    );
  }

  created() {
    this.handlePackageComponentSetup();
  }

  @Watch('isPackageTabOpened')
  handlePackageComponentSetup() {
    // Only perform course package related changes when the tab is open
    if (this.isPackageTabOpened) {
      // Only add course pacakges if no course packages found
      if (this.activeCoursePackages.length === 0) {
        this.addCoursePackages();
      }

      /**
       * Default course packages are assigned into the UI as first initial course package component.
       * As such, the component will already have existing ID, and can directly call the upsert endpoint.
       */
      if (
        this.activeCoursePackages.length === 1 &&
        this.hasDefaultCoursePackage
      ) {
        this.upsertCoursePackages(
          this.constructInitialCoursePackagePayload(
            this.hasDefaultCoursePackage
          )
        );
      }
    }
  }

  constructInitialCoursePackagePayload(
    isDefault: boolean,
    tempId?: number
  ): UpsertCoursePackageApiDTO {
    if (isDefault) {
      return {
        courseId: Number(this.courseDetails.id),
        coursePackageId:
          this.activeCoursePackages.length === 1 &&
          this.activeCoursePackages[0].status === CoursePackageStatus.DEFAULT
            ? this.activeCoursePackages[0].id!
            : undefined,
        courseModulesIds: []
      };
    }
    return {
      courseId: Number(this.courseDetails.id),
      coursePackageId: tempId,
      courseModulesIds: []
    };
  }

  // Adds default course module values for dynamically rendered components
  addCoursePackages() {
    const coursePackageToAdd: CoursePackageComponent = defaultCoursePackage();
    this.addActiveCoursePackage(coursePackageToAdd);
    this.upsertCoursePackages(
      this.constructInitialCoursePackagePayload(false, coursePackageToAdd.id!)
    );
  }

  removeCoursePackage(indexToRemove: number) {
    const coursePackageToDelete: CoursePackageComponent = this
      .activeCoursePackages[indexToRemove];
    this.removeActiveCoursePackage(indexToRemove);
    this.deleteCoursePackages([coursePackageToDelete.id!]);
  }

  @Debounce(1500)
  handleUpsertCoursePackage(indexToUpdate: number) {
    // Retrieve active course package from state
    const coursePackageToUpdate = this.activeCoursePackages[indexToUpdate];

    const payload: UpsertCoursePackageApiDTO = {
      courseId: Number(this.courseDetails.id),
      coursePackageId: coursePackageToUpdate.id!,
      courseModulesIds: coursePackageToUpdate.courseModules.map((cm) => cm.id!),
      amount: coursePackageToUpdate.amount,
      currency: coursePackageToUpdate.currency
    };

    this.upsertCoursePackages(payload);
  }

  /**
   *  After the upsert API call completes, the backend response includes this same
   *  `tempId` along with the newly created module object (which now has a real,
   *  positive database ID). We then use the returned `tempId` to find the correct
   *  placeholder component in our state array and update it with the real ID and
   *  other server-generated data.
   */
  // TODO: Ideally just re-render the tab, and none of the swapping nonsense. However,
  // current auto-save implementation, may cause update loops. In future, rework update
  // cycle to have a 'queue' which adds all updated attributes and performs updates
  // asynchronously.
  updateExistingCoursePackage() {
    if (this.upsertedCoursePackage) {
      // Convert API response into course package component for frontend
      const itemToUpdateIndex = this.activeCoursePackages.findIndex(
        (pkg) => pkg.id === this.upsertedCoursePackage.tempId
      );

      if (itemToUpdateIndex > -1) {
        const updatePayload: UpdateCoursePackageStatePayload = {
          index: itemToUpdateIndex,
          data: { id: this.upsertedCoursePackage.realId }
        };

        this.updateCoursePackage(updatePayload);
      }
    }
  }

  @Watch('activeCoursePackages', { deep: true })
  validateOverallFormErrorState() {
    // Assume there is error because course modules tab has not been visited
    if (this.activeCoursePackages.length === 1 && !this.isPackageTabOpened) {
      this.updateCourseModuleFormError(true);
    }
  }

  @Watch('upsertCoursePackageApiState', { deep: true })
  onApiStateChanged(state: ApiState) {
    if (state.success) {
      Toast.open({
        type: 'is-success',
        position: 'is-top',
        message: 'Changes saved.',
        duration: 1000,
        queue: false
      });

      this.updateExistingCoursePackage();
    }

    if (state.error) {
      Toast.open({
        queue: true,
        type: 'is-danger',
        position: 'is-top',
        message: 'Something went wrong. Could not process course packages.'
      });
    }
  }
}
